import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Validation utilities
const validators = {
  sanitizeInput: (input) => {
    if (!input) return '';
    // Remove all HTML tags and scripts. Do NOT trim here — this runs on every
    // keystroke, and trimming the trailing space mid-typing eats spaces between
    // words. Trimming happens once at submit instead.
    return input
      .replace(/<[^>]*>/g, '')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  },

  // Check if input contains HTML
  containsHTML: (input) => {
    if (!input) return false;
    const htmlPattern = /<[a-z][\s\S]*>/i;
    const scriptPattern = /<script|javascript:|onclick|onerror|onload/i;
    return htmlPattern.test(input) || scriptPattern.test(input);
  },

  // Validate phone number (US format)
  isValidPhone: (phone) => {
    if (!phone) return false;
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    // Must be 10 digits (US) or 11 digits (1 + area code)
    return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
  },

  // Format phone number for display
  formatPhone: (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return phone;
  },

  // Validate email
  isValidEmail: (email) => {
    if (!email) return false;
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  }
};

const AppointmentForm = ({ source = 'main_website' }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    referral_source: '',
    preferred_day: '',
    preferred_time: '',
    patient_type: '',
    contact_preference: 'call', // 'call', 'text', or 'email'
    is_vendor: false,
    message: '',
    // Honeypot field for bot detection (should remain empty)
    website: '',
    // Vendor-specific fields
    vendor_company: '',
    vendor_phone: '',
    vendor_url: '',
    vendor_position: '',
    vendor_address: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formStartTime] = useState(Date.now());

  // Handle input change with sanitization
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    
    // Sanitize text inputs
    const sanitizedValue = validators.sanitizeInput(value);
    
    // Clear field error when user types
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
    
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
  }, []);

  // Handle phone input with formatting
  const handlePhoneChange = useCallback((e) => {
    const { value } = e.target;
    // Only allow digits, spaces, dashes, parentheses
    const cleaned = value.replace(/[^\d\s\-()]/g, '');
    
    setFieldErrors(prev => ({ ...prev, phone: '' }));
    setFormData(prev => ({ ...prev, phone: cleaned }));
  }, []);

  // Validate all fields before submit
  const validateForm = () => {
    const errors = {};

    // Check for HTML in any text field
    const textFields = ['name', 'message'];
    for (const field of textFields) {
      if (validators.containsHTML(formData[field])) {
        errors[field] = 'Invalid characters detected. Please remove any HTML or special formatting.';
      }
    }

    // Validate phone
    if (!validators.isValidPhone(formData.phone)) {
      errors.phone = 'Please enter a valid 10-digit phone number.';
    }

    // Validate email
    if (!validators.isValidEmail(formData.email)) {
      errors.email = 'Please enter a valid email address.';
    }

    // Check required fields
    if (!formData.name.trim()) errors.name = 'Name is required.';
    if (!formData.referral_source) errors.referral_source = 'Please select how you heard about us.';
    if (!formData.preferred_day) errors.preferred_day = 'Please select a preferred day.';
    if (!formData.preferred_time) errors.preferred_time = 'Please select a preferred time.';
    if (!formData.patient_type) errors.patient_type = 'Please select if you are a new or existing patient.';

    // Vendor-specific required fields
    if (formData.patient_type === 'vendor') {
      // Business name validation - must be a real business name, not a domain/URL
      const companyName = formData.vendor_company.trim().toLowerCase();
      if (!formData.vendor_company.trim()) {
        errors.vendor_company = 'Company name is required for vendors.';
      } else if (companyName.includes('.com') || companyName.includes('.net') || companyName.includes('.org') || 
                 companyName.includes('.io') || companyName.includes('http') || companyName.includes('www.')) {
        errors.vendor_company = 'Please enter your actual business name, not a website URL.';
      } else if (companyName.length < 3) {
        errors.vendor_company = 'Please enter a valid business name (minimum 3 characters).';
      }
      
      // Position/Title validation - cannot be n/a, must be legitimate
      const position = formData.vendor_position.trim().toLowerCase();
      if (!formData.vendor_position.trim()) {
        errors.vendor_position = 'Your position/title is required for vendors.';
      } else if (position === 'n/a' || position === 'na' || position === 'none' || 
                 position === '-' || position === '.' || position.length < 3) {
        errors.vendor_position = 'Please enter your actual job title (e.g., Sales Manager, Account Executive).';
      }
      
      // Company phone validation
      if (!formData.vendor_phone.trim()) {
        errors.vendor_phone = 'Company phone is required for vendors.';
      } else if (!validators.isValidPhone(formData.vendor_phone)) {
        errors.vendor_phone = 'Please enter a valid 10-digit company phone number.';
      }
      
      // Company website validation
      if (!formData.vendor_url.trim()) {
        errors.vendor_url = 'Company website is required for vendors.';
      } else if (!formData.vendor_url.includes('.') || formData.vendor_url.length < 5) {
        errors.vendor_url = 'Please enter a valid company website URL.';
      }
      
      // Business address validation - required
      if (!formData.vendor_address.trim()) {
        errors.vendor_address = 'Business address is required for vendors.';
      } else if (formData.vendor_address.trim().length < 10) {
        errors.vendor_address = 'Please enter a complete business address.';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Bot detection: Honeypot check
    if (formData.website) {
      // Bot filled in the honeypot field - silently reject
      setSuccess(true);
      return;
    }

    // Bot detection: Time-based check (form filled too quickly)
    const timeTaken = Date.now() - formStartTime;
    if (timeTaken < 3000) { // Less than 3 seconds
      setSuccess(true);
      return;
    }

    // Validate form
    if (!validateForm()) {
      setError('Please correct the errors above.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Capture browser/device info for debugging
      const browserInfo = {
        user_agent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        is_mobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
        is_touch: 'ontouchstart' in window || navigator.maxTouchPoints > 0
      };

      // Prepare data with sanitized values
      const submitData = {
        ...formData,
        name: validators.sanitizeInput(formData.name).trim(),
        message: validators.sanitizeInput(formData.message),
        phone: formData.phone.replace(/\D/g, ''), // Send only digits
        source: source, // Identifies which form/page this came from
        browser_info: browserInfo, // For debugging form issues
        // Include vendor fields if it's a vendor submission
        vendor_company: formData.patient_type === 'vendor' ? formData.vendor_company : '',
        vendor_phone: formData.patient_type === 'vendor' ? formData.vendor_phone.replace(/\D/g, '') : '',
        vendor_url: formData.patient_type === 'vendor' ? formData.vendor_url : '',
        vendor_position: formData.patient_type === 'vendor' ? formData.vendor_position : '',
        vendor_address: formData.patient_type === 'vendor' ? formData.vendor_address : '',
      };

      await axios.post(`${API}/appointments`, submitData);
      setSuccess(true);
      setFormData({
        name: '',
        phone: '',
        email: '',
        referral_source: '',
        preferred_day: '',
        preferred_time: '',
        patient_type: '',
        contact_preference: 'call',
        is_vendor: false,
        message: '',
        website: '',
        vendor_company: '',
        vendor_phone: '',
        vendor_url: '',
        vendor_position: '',
        vendor_address: ''
      });
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to submit appointment. Please try again or call us directly.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-8 rounded-lg shadow-lg border-l-4 border-teal-500">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Honeypot field - hidden from users, bots will fill it */}
        <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
          <label htmlFor="website_field">Leave this field empty</label>
          <input
            type="text"
            name="website"
            id="website_field"
            value={formData.website}
            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            tabIndex="-1"
            autoComplete="off"
          />
        </div>

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            Thank you! Your appointment request has been submitted. We will contact you soon.
          </div>
        )}
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Row 1: Name and Phone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name">Name <span className="text-red-500">*</span></Label>
            <Input
              id="name"
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Your full name"
              className={`mt-1 ${fieldErrors.name ? 'border-red-500' : ''}`}
              maxLength={100}
            />
            {fieldErrors.name && <p className="text-red-500 text-xs mt-1">{fieldErrors.name}</p>}
          </div>
          <div>
            <Label htmlFor="phone">Phone <span className="text-red-500">*</span></Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              required
              value={formData.phone}
              onChange={handlePhoneChange}
              placeholder="(817) 555-1234"
              className={`mt-1 ${fieldErrors.phone ? 'border-red-500' : ''}`}
              maxLength={20}
            />
            {fieldErrors.phone && <p className="text-red-500 text-xs mt-1">{fieldErrors.phone}</p>}
          </div>
        </div>

        {/* Row 2: Email and How did you hear about us */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@example.com"
              className={`mt-1 ${fieldErrors.email ? 'border-red-500' : ''}`}
              maxLength={100}
            />
            {fieldErrors.email && <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>}
          </div>
          <div>
            <Label htmlFor="referral_source">How did you hear about us? <span className="text-red-500">*</span></Label>
            <Select
              value={formData.referral_source}
              onValueChange={(value) => {
                setFieldErrors(prev => ({ ...prev, referral_source: '' }));
                setFormData({ ...formData, referral_source: value });
              }}
              required
            >
              <SelectTrigger className={`mt-1 ${fieldErrors.referral_source ? 'border-red-500' : ''}`}>
                <SelectValue placeholder="Select one..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Search Engine">Search Engine</SelectItem>
                <SelectItem value="Family/Friend">Family/Friend</SelectItem>
                <SelectItem value="Promotion">Promotion</SelectItem>
                <SelectItem value="Social Media">Social Media</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
            {fieldErrors.referral_source && <p className="text-red-500 text-xs mt-1">{fieldErrors.referral_source}</p>}
          </div>
        </div>

        {/* Row 3: Preferred day and Preferred time */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="preferred_day">Preferred day of the week <span className="text-red-500">*</span></Label>
            <Select
              value={formData.preferred_day}
              onValueChange={(value) => {
                setFieldErrors(prev => ({ ...prev, preferred_day: '' }));
                setFormData({ ...formData, preferred_day: value });
              }}
              required
            >
              <SelectTrigger className={`mt-1 ${fieldErrors.preferred_day ? 'border-red-500' : ''}`}>
                <SelectValue placeholder="Select day..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Monday">Monday</SelectItem>
                <SelectItem value="Tuesday">Tuesday</SelectItem>
                <SelectItem value="Wednesday">Wednesday</SelectItem>
                <SelectItem value="Thursday">Thursday</SelectItem>
                <SelectItem value="Friday">Friday</SelectItem>
              </SelectContent>
            </Select>
            {fieldErrors.preferred_day && <p className="text-red-500 text-xs mt-1">{fieldErrors.preferred_day}</p>}
          </div>
          <div>
            <Label htmlFor="preferred_time">Preferred time of the day <span className="text-red-500">*</span></Label>
            <Select
              value={formData.preferred_time}
              onValueChange={(value) => {
                setFieldErrors(prev => ({ ...prev, preferred_time: '' }));
                setFormData({ ...formData, preferred_time: value });
              }}
              required
            >
              <SelectTrigger className={`mt-1 ${fieldErrors.preferred_time ? 'border-red-500' : ''}`}>
                <SelectValue placeholder="Select time..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Morning">Morning</SelectItem>
                <SelectItem value="Afternoon">Afternoon</SelectItem>
              </SelectContent>
            </Select>
            {fieldErrors.preferred_time && <p className="text-red-500 text-xs mt-1">{fieldErrors.preferred_time}</p>}
          </div>
        </div>

        {/* Row 4: Patient Type */}
        <div>
          <Label>Are you... <span className="text-red-500">*</span></Label>
          <RadioGroup
            value={formData.patient_type}
            onValueChange={(value) => {
              setFieldErrors(prev => ({ ...prev, patient_type: '' }));
              setFormData({ ...formData, patient_type: value });
            }}
            className="flex flex-wrap gap-4 mt-2"
            required
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="new" id="patient-new" aria-label="A New Patient" />
              <Label htmlFor="patient-new" className="font-normal cursor-pointer">A New Patient</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="existing" id="patient-existing" aria-label="An Existing Patient" />
              <Label htmlFor="patient-existing" className="font-normal cursor-pointer">An Existing Patient</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="vendor" id="patient-vendor" aria-label="A Vendor" />
              <Label htmlFor="patient-vendor" className="font-normal cursor-pointer">A Vendor</Label>
            </div>
          </RadioGroup>
          {fieldErrors.patient_type && <p className="text-red-500 text-xs mt-1">{fieldErrors.patient_type}</p>}
        </div>

        {/* Vendor-Specific Fields - Only show when vendor is selected */}
        {formData.patient_type === 'vendor' && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4">
            <p className="text-sm text-amber-800 font-medium">Please provide your company information (all fields required):</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="vendor_company">Company/Business Name <span className="text-red-500">*</span></Label>
                <Input
                  id="vendor_company"
                  name="vendor_company"
                  type="text"
                  required
                  value={formData.vendor_company}
                  onChange={handleChange}
                  placeholder="e.g., ABC Dental Supplies Inc."
                  className={`mt-1 ${fieldErrors.vendor_company ? 'border-red-500' : ''}`}
                  maxLength={100}
                />
                {fieldErrors.vendor_company && <p className="text-red-500 text-xs mt-1">{fieldErrors.vendor_company}</p>}
                <p className="text-xs text-gray-500 mt-1">Enter your legal business name, not a website</p>
              </div>
              <div>
                <Label htmlFor="vendor_position">Your Position/Title <span className="text-red-500">*</span></Label>
                <Input
                  id="vendor_position"
                  name="vendor_position"
                  type="text"
                  required
                  value={formData.vendor_position}
                  onChange={handleChange}
                  placeholder="e.g., Regional Sales Manager"
                  className={`mt-1 ${fieldErrors.vendor_position ? 'border-red-500' : ''}`}
                  maxLength={100}
                />
                {fieldErrors.vendor_position && <p className="text-red-500 text-xs mt-1">{fieldErrors.vendor_position}</p>}
                <p className="text-xs text-gray-500 mt-1">Enter your actual job title</p>
              </div>
            </div>
            
            <div>
              <Label htmlFor="vendor_address">Business Address <span className="text-red-500">*</span></Label>
              <Input
                id="vendor_address"
                name="vendor_address"
                type="text"
                required
                value={formData.vendor_address}
                onChange={handleChange}
                placeholder="123 Main St, Suite 100, City, State ZIP"
                className={`mt-1 ${fieldErrors.vendor_address ? 'border-red-500' : ''}`}
                maxLength={200}
              />
              {fieldErrors.vendor_address && <p className="text-red-500 text-xs mt-1">{fieldErrors.vendor_address}</p>}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="vendor_phone">Company Phone Number <span className="text-red-500">*</span></Label>
                <Input
                  id="vendor_phone"
                  name="vendor_phone"
                  type="tel"
                  required
                  value={formData.vendor_phone}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^\d\s\-()]/g, '');
                    setFieldErrors(prev => ({ ...prev, vendor_phone: '' }));
                    setFormData(prev => ({ ...prev, vendor_phone: cleaned }));
                  }}
                  placeholder="(817) 555-1234"
                  className={`mt-1 ${fieldErrors.vendor_phone ? 'border-red-500' : ''}`}
                  maxLength={20}
                />
                {fieldErrors.vendor_phone && <p className="text-red-500 text-xs mt-1">{fieldErrors.vendor_phone}</p>}
              </div>
              <div>
                <Label htmlFor="vendor_url">Company Website <span className="text-red-500">*</span></Label>
                <Input
                  id="vendor_url"
                  name="vendor_url"
                  type="url"
                  required
                  value={formData.vendor_url}
                  onChange={(e) => {
                    setFieldErrors(prev => ({ ...prev, vendor_url: '' }));
                    setFormData(prev => ({ ...prev, vendor_url: e.target.value }));
                  }}
                  placeholder="https://yourcompany.com"
                  className={`mt-1 ${fieldErrors.vendor_url ? 'border-red-500' : ''}`}
                  maxLength={200}
                />
                {fieldErrors.vendor_url && <p className="text-red-500 text-xs mt-1">{fieldErrors.vendor_url}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Row 5: Contact Preference - Only show for non-vendors */}
        {formData.patient_type !== 'vendor' && (
          <div>
            <Label>Preferred contact method</Label>
            <RadioGroup
              value={formData.contact_preference}
              onValueChange={(value) => setFormData({ ...formData, contact_preference: value })}
              className="flex flex-wrap gap-4 mt-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="call" id="contact-call" aria-label="Phone Call" />
                <Label htmlFor="contact-call" className="font-normal cursor-pointer">Phone Call</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="text" id="contact-text" aria-label="Text Message" />
                <Label htmlFor="contact-text" className="font-normal cursor-pointer">Text Message</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="email" id="contact-email" aria-label="Email" />
                <Label htmlFor="contact-email" className="font-normal cursor-pointer">Email</Label>
              </div>
            </RadioGroup>
            
            {/* 10DLC Compliance Notice - Show when Text is selected */}
            {formData.contact_preference === 'text' && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <strong>SMS Consent Notice:</strong> By selecting text message, you consent to receive SMS messages from Fort Worth Dental regarding your appointment. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe or HELP for assistance. View our <a href="/privacy" className="underline">Privacy Policy</a> and <a href="/terms" className="underline">Terms of Service</a>.
              </div>
            )}
          </div>
        )}

        {/* Row 6: Message */}
        <div>
          <Label htmlFor="message">Message {formData.patient_type === 'vendor' && <span className="text-gray-500">(Please describe your services)</span>}</Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder={formData.patient_type === 'vendor' 
              ? "Please describe your services and how you can help our practice..." 
              : "Tell us more about your dental needs..."}
            rows={4}
            className={`mt-1 ${fieldErrors.message ? 'border-red-500' : ''}`}
            maxLength={1000}
          />
          {fieldErrors.message && <p className="text-red-500 text-xs mt-1">{fieldErrors.message}</p>}
          <p className="text-xs text-gray-500 mt-1">{formData.message.length}/1000 characters</p>
        </div>

        {/* Vendor Notice */}
        {formData.patient_type === 'vendor' && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            <strong>Vendor Notice:</strong> Your inquiry will be sent to our business development team for review. We will contact you if your services align with our practice needs.
          </div>
        )}

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3"
          data-testid="appointment-form-submit"
        >
          {loading ? 'Submitting...' : 'Send Request'}
        </Button>

        <p className="text-xs text-gray-500 text-center">
          Your information is secure and will never be shared with third parties.
        </p>
      </form>
    </div>
  );
};

export default AppointmentForm;
